import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Check, Users, ChevronDown, ChevronUp } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import api from "@/lib/api";

export default function ReferralPanel() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  const [showFriends, setShowFriends] = useState(false);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.invite_url);
      setCopied(true);
      toast.success("Invite link copied — share it with a friend!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (!data) {
    return (
      <div data-testid="referral-panel-loading"
        className="border border-amber-900/30 bg-zinc-950 p-3 animate-pulse text-[11px] uppercase tracking-widest text-zinc-600">
        Loading referrals…
      </div>
    );
  }

  return (
    <div data-testid="referral-panel"
      className="relative overflow-hidden border border-amber-700/40 bg-gradient-to-br from-amber-950/25 to-zinc-950 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <Gift size={11} className="text-amber-400" /> Invite a Friend
        </span>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
          <span data-testid="referral-count" className="text-amber-300 flex items-center gap-1">
            <Users size={11} /> {data.total_referrals}
          </span>
          <span data-testid="referral-gold-earned" className="text-emerald-400 flex items-center gap-1">
            <GoldCoin size={11} /> {data.gold_earned}
          </span>
        </div>
      </div>

      <div className="flex items-stretch gap-2">
        <input data-testid="referral-link-input" readOnly value={data.invite_url}
          className="flex-1 min-w-0 bg-zinc-900 border border-amber-900/40 px-2.5 py-1.5 text-[11px] text-zinc-300 truncate rounded-sm" />
        <button data-testid="referral-copy-btn" onClick={copy}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm">
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <p className="text-[11px] text-zinc-500 mt-2 leading-snug">
        You <span className="text-amber-300/90">both</span> earn escalating rewards as your recruit battles on.
      </p>

      <div className="mt-2 flex items-center gap-4">
        <button data-testid="referral-tiers-toggle" onClick={() => setShowTiers((s) => !s)}
          className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 transition-colors">
          {showTiers ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Rewards
        </button>
        {data.invitees.length > 0 && (
          <button data-testid="referral-friends-toggle" onClick={() => setShowFriends((s) => !s)}
            className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 transition-colors">
            {showFriends ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Recruits ({data.invitees.length})
          </button>
        )}
      </div>

      {showTiers && (
        <div className="mt-2 space-y-1">
          {data.tiers.map((t, i) => (
            <div key={t.id} data-testid={`referral-tier-${t.id}`}
              className="flex items-start gap-2 border border-zinc-800/70 bg-zinc-950/50 px-2 py-1.5 rounded-sm">
              <span className="shrink-0 w-4 h-4 flex items-center justify-center text-[9px] font-cinzel border border-amber-700/50 text-amber-400 rounded-sm mt-0.5">{i + 1}</span>
              <div className="min-w-0">
                <div className="text-[11px] font-cinzel text-amber-200 tracking-wide">{t.title}</div>
                <div className="text-[10px] text-zinc-500 leading-snug">{t.requirement}</div>
                <div className="text-[10px] text-emerald-400">Each: {t.reward}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showFriends && data.invitees.length > 0 && (
        <div data-testid="referral-friends-list" className="mt-2 space-y-1">
          {data.invitees.map((f, i) => (
            <div key={i} className="flex items-center justify-between border border-zinc-800/70 bg-zinc-950/50 px-2 py-1 rounded-sm text-[10px]">
              <span className="text-zinc-200 font-medium truncate flex items-center gap-1.5">
                {f.name}
                {f.blocked_same_ip && (
                  <span data-testid="referral-blocked-tag" title="Rewards disabled — this recruit signed up from the same network as you."
                    className="text-[8px] uppercase tracking-wide text-red-400 border border-red-800/50 bg-red-950/30 px-1 py-0.5 rounded-sm">
                    No bonus · same IP
                  </span>
                )}
              </span>
              <span className="text-zinc-500 shrink-0 ml-2">
                {f.glad_count}g · {f.battles}b · {f.tiers_paid.length}/{data.tiers.length}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
